import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import axios from 'axios';
import { ArrowLeft, Volume2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { withAudioVersion } from '../utils/audioUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Catégories en mode "Immersion Image" - masque les traductions
const IMMERSION_CATEGORIES = [
  'couleurs', 'colors',
  'maison', 'house', 'home',
  'fruits', 'légumes', 'fruits et légumes', 'vegetables', 'fruits and vegetables',
  'nourriture', 'food',
  'lieux', 'places', 'locations',
  'plats', 'dishes', 'meals',
  'moyens de transport', 'transport', 'transportation',
  'sport', 'sports',
  'école', 'school', 'education',
  'vêtements', 'clothes', 'clothing'
];

// Fonction pour vérifier si une catégorie est en mode immersion
const isImmersionCategory = (category) => {
  if (!category) return false;
  const normalizedCategory = category.toLowerCase().trim();
  return IMMERSION_CATEGORIES.some(immCat => 
    normalizedCategory.includes(immCat) || immCat.includes(normalizedCategory)
  );
};

const Vocabulary = () => {
  const { token } = useAuth();
  const { scriptMode } = usePreferences();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [vocabulary, setVocabulary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideTranslationMap, setHideTranslationMap] = useState({});

  useEffect(() => {
    fetchCategories();
    fetchHideTranslationSettings();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchVocabulary();
    }
  }, [selectedCategory]);

  // Per-level hide_translation settings saved by the admin (Réorganisation du Contenu).
  // If these fail to load the map stays empty and we fall back to IMMERSION_CATEGORIES.
  const fetchHideTranslationSettings = async () => {
    const levels = ['beginner', 'intermediate', 'advanced'];
    const results = await Promise.all(
      levels.map((lvl) => axios.get(`${API}/category-order/${lvl}`).catch(() => null))
    );
    const map = {};
    results.forEach((res, i) => {
      const entries = res?.data?.categories || [];
      map[levels[i]] = entries.reduce((acc, c) => {
        if (c && typeof c.hide_translation === 'boolean') {
          acc[c.category] = c.hide_translation;
        }
        return acc;
      }, {});
    });
    setHideTranslationMap(map);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/vocabulary/categories`);
      setCategories(response.data.categories);
      if (response.data.categories.length > 0) {
        setSelectedCategory(response.data.categories[0]);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const fetchVocabulary = async () => {
    try {
      const response = await axios.get(`${API}/vocabulary`, {
        params: { category: selectedCategory }
      });
      setVocabulary(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du vocabulaire');
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      numbers: 'Nombres',
      greetings: 'Salutations',
      colors: 'Couleurs',
      family: 'Famille',
      food: 'Nourriture'
    };
    return labels[cat] || cat;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <nav className="bg-white border-b-2 border-stone-200 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="tile-button bg-stone-200 text-charcoal p-2 rounded-lg hover:bg-stone-300"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="font-playfair text-3xl font-bold text-primary" data-testid="vocabulary-title">
            Vocabulaire
          </h1>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-wrap gap-3 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat}`}
              className={`tile-button font-nunito font-bold py-2 px-6 rounded-lg ${
                selectedCategory === cat
                  ? 'bg-primary text-white shadow-[0_3px_0_#9a360b]'
                  : 'bg-white text-charcoal border-2 border-stone-200 hover:border-primary'
              }`}
            >
              {getCategoryLabel(cat)}
            </button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {vocabulary.map((item, index) => {
            const immersionMode =
              hideTranslationMap[item.level]?.[item.category] ??
              isImmersionCategory(item.category);
            
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                data-testid={`vocab-card-${index}`}
                className="bg-white border-2 border-stone-200 rounded-xl p-6 shadow-sm hover:shadow-md card-hover"
              >
                {/* Mode Immersion Image */}
                {immersionMode && item.image_url && (
                  <div className="mb-4 rounded-xl overflow-hidden bg-stone-100 shadow-inner">
                    <img 
                      src={item.image_url} 
                      alt={item.word_french}
                      referrerPolicy="no-referrer"
                      className="w-full h-48 object-contain bg-stone-100"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Mode Immersion sans image - placeholder stylé */}
                {immersionMode && !item.image_url && (
                  <div className="mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-stone-100 to-stone-200 h-48 flex items-center justify-center">
                    <div className="text-center">
                      <ImageIcon className="w-12 h-12 text-stone-400 mx-auto mb-2" />
                      <p className="text-sm text-stone-400 font-nunito">Image à ajouter</p>
                    </div>
                  </div>
                )}

                <div className="text-center mb-4">
                  {scriptMode === 'arabic' ? (
                    <p className="font-amiri text-4xl mb-2 text-primary">{item.word_darija_arabic}</p>
                  ) : (
                    <p className="font-nunito text-3xl font-bold mb-2 text-primary">{item.word_darija_phonetic}</p>
                  )}
                </div>
                
                {/* Traductions - masquées en mode immersion */}
                {!immersionMode && (
                  <div className="space-y-2 text-center">
                    <p className="font-nunito text-lg text-charcoal">
                      <span className="font-semibold">🇫🇷</span> {item.word_french}
                    </p>
                    <p className="font-nunito text-lg text-charcoal">
                      <span className="font-semibold">🇬🇧</span> {item.word_english}
                    </p>
                  </div>
                )}

                {/* Image pour catégories non-immersion si disponible */}
                {!immersionMode && item.image_url && (
                  <div className="mt-3 rounded-lg overflow-hidden bg-stone-100">
                    <img 
                      src={item.image_url} 
                      alt={item.word_french}
                      referrerPolicy="no-referrer"
                      className="w-full h-32 object-contain bg-stone-100"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <div className={`${immersionMode ? 'mt-4' : 'mt-4 pt-4 border-t border-stone-200'}`}>
                  <button
                    data-testid={`audio-btn-${index}`}
                    className="w-full tile-button bg-accent text-charcoal font-nunito font-semibold py-2 px-4 rounded-lg shadow-[0_3px_0_#d4941f] active:shadow-none flex items-center justify-center gap-2"
                    onClick={() => {
                      if (item.audio_url) {
                        const audio = new Audio(withAudioVersion(item.audio_url.startsWith('http') ? item.audio_url : `${BACKEND_URL}${item.audio_url}`));
                        audio.play().catch(() => toast.error('Erreur de lecture audio'));
                      } else {
                        toast.info('Audio non disponible pour ce mot');
                      }
                    }}
                  >
                    <Volume2 className="w-5 h-5" />
                    Écouter
                  </button>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {vocabulary.length === 0 && (
          <div className="text-center py-12">
            <p className="font-nunito text-xl text-charcoal/60">Aucun mot dans cette catégorie</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Vocabulary;